import React, { useEffect, useState } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { JobMode } from "../../../../../../../../models/job.mode";
import { Status } from "../../../../../../../../util/enums";
import Input from "../../../../../../../../components/Form/Input";
import RadioGroup from "../../../../../../../../components/Form/RadioGroup";
import { ListColor } from "../../../../../../../../components/Generic/ListColor";
import Modal from "../../../../../../../../components/Generic/Modal";
import useStyles from "./styles";

interface Props {
  modeToUpdate?: JobMode;
  open: boolean;
  hasError?: boolean;
  isLoading: boolean;
  closeModal: () => void;
  submitModeType: (mode: JobMode) => void;
  updateMode?: (mode: JobMode) => void;
  createMode?: (mode: JobMode) => void;
}

const ModeTypeModal = ({
  modeToUpdate,
  open,
  hasError,
  isLoading,
  closeModal,
  submitModeType,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [modeName, setModeName] = useState<string>("");
  const [modeCode, setModeCode] = useState<string>("");
  const [modeStatus, setModeStatus] = useState<string>(Status.INACTIVE);
  const [modeIsDestination, setModeIsDestination] = useState<boolean>(false);
  const [modeColor, setModeColor] = useState<string | null>(null);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    modeName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (modeName) =>
        modeName ? modeName !== "" : false
      ),
    modeStatus: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (modeStatus) =>
        modeStatus ? modeStatus !== "" : false
      ),
    modeCode: yup.string(),
    modeIsDestination: yup.boolean(),
    modeColor: yup
      .string()
      .nullable()
      .when("modeIsDestination", {
        is: true,
        then: yup.string().nullable().required(translate("thisFieldIsRequired")),
      }),
    icon: yup.string(),
  });

  const clearFields = () => {
    setModeName("");
    setModeStatus("");
    setModeCode("");
    setModeColor("");
    setModeIsDestination(false);
  };

  const handleValidateSuccess = (mode?: JobMode): boolean => {
    schema.validateSync(
      {
        modeName: mode?.name,
        modeStatus: mode?.status,
        modeCode: mode?.code,
        modeIsDestination: mode?.isDestination,
        modeColor: mode?.color,
      },
      { abortEarly: false }
    );

    setErrors([]);

    return true;
  };

  const handleValidateError = (err: any): boolean => {
    if (err instanceof yup.ValidationError) setErrors(err?.inner);

    return false;
  };

  const isValid = (mode?: JobMode) => {
    try {
      return handleValidateSuccess(mode);
    } catch (error) {
      return handleValidateError(error);
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const object = {
      name: modeName,
      value: modeName,
      status: modeStatus,
      code: modeCode,
      isDestination: modeIsDestination,
      color: modeColor,
    } as JobMode;

    if (isValid(object)) submitModeType(object);
  };

  useEffect(() => {
    if (modeToUpdate) {
      setModeName(modeToUpdate.name);
      setModeStatus(modeToUpdate.status);
      setModeCode(modeToUpdate.code || "");
      setModeIsDestination(modeToUpdate?.isDestination || false);
      setModeColor(modeToUpdate?.color || "");
    }
  }, [modeToUpdate]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      const object = {
        name: modeName,
        code: modeCode,
        status: modeStatus,
        isDestination: modeIsDestination,
        color: modeColor,
      } as JobMode;

      isValid(object);
    }
  }, [modeName, modeCode, modeStatus, modeIsDestination, modeColor, hasFormBeenSubmitted]);

  return (
    <Modal
      data-cy="modeTypeModal"
      open={open}
      handleClose={closeModal}
      title={modeToUpdate ? translate("editModeType") : translate("createModeType")}
      showActionButtons
      actionButtonProps={{
        textSave: modeToUpdate ? translate("update") : translate("create"),
        dataCySave: "submitButton",
        loading: isLoading,
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        onSave: handleSubmit,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("modeTypeStatus"),
        value: modeStatus,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setModeStatus(value),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="modeCode"
              label={translate("code")}
              placeholder={translate("code")}
              error={!!getErrorByField("modeCode")}
              errorMessage={getErrorByField("modeCode")}
              value={modeCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setModeCode(e.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="modeName"
              label={translate("name")}
              placeholder={translate("name")}
              error={!!getErrorByField("modeName")}
              errorMessage={getErrorByField("modeName")}
              value={modeName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setModeName(e.target.value)}
            />
          </Grid>

          <Grid item md={6} lg={6} container direction="column">
            <span className={classes.label}>{translate("isThisAnItemDestination")}</span>

            <RadioGroup
              aria-label="mode-is-destination"
              name="radio-mode-is-destination"
              data-cy="radio-mode-is-destination"
              value={`${modeIsDestination}`}
              onChange={(event) => {
                const isDestination = !!(event.target.value === "true");

                if (!isDestination) {
                  setModeColor(null);
                }

                setModeIsDestination(isDestination);
              }}
              options={[
                {
                  label: translate("yes"),
                  value: "true",
                  "data-cy": "radio-mode-is-destination-yes",
                },
                {
                  label: translate("no"),
                  value: "false",
                  "data-cy": "radio-mode-is-destination-no",
                },
              ]}
            />
          </Grid>

          {modeIsDestination && (
            <Grid
              item
              container
              md={12}
              lg={12}
              alignItems="flex-start"
              spacing={1}
              direction="column"
            >
              <span className={classes.label}>{translate("color")}</span>

              <ListColor
                error={!!getErrorByField("modeColor")}
                errorMessage={getErrorByField("modeColor")}
                colorSelected={modeColor || ""}
                onChangeColor={(color) => setModeColor(color)}
              />
            </Grid>
          )}
        </Grid>
      </section>
    </Modal>
  );
};

export default ModeTypeModal;
